import React from 'react';
import { AppAlert, AppButton, AppModal } from '../ui';
import useAppConfirm from '../../hooks/useAppConfirm';

/**
 * Modal de choix du mode d'import Excel des plannings de ventes.
 *
 * @param {{
 *   show: boolean,
 *   title: string,
 *   sheetHint: string,
 *   canalLabel: string,
 *   strategy: 'upsert' | 'wipe',
 *   onStrategyChange: (strategy: 'upsert' | 'wipe') => void,
 *   wipeScope: 'all' | 'month' | 'year',
 *   onWipeScopeChange: (scope: 'all' | 'month' | 'year') => void,
 *   allowYearScope?: boolean,
 *   periodLabel?: string,
 *   onClose: () => void,
 *   onConfirm: () => void,
 * }} props
 */
const KkpPlanningImportModal = ({
  show,
  title,
  sheetHint,
  canalLabel,
  strategy,
  onStrategyChange,
  wipeScope,
  onWipeScopeChange,
  allowYearScope = false,
  periodLabel = '',
  onClose,
  onConfirm,
}) => {
  const { ask, modal } = useAppConfirm();
  if (!show) {
    return null;
  }

  const confirmLabel = strategy === 'wipe'
    ? (wipeScope === 'all'
      ? 'Choisir le fichier et tout remplacer'
      : periodLabel
        ? `Choisir le fichier et remplacer ${periodLabel}`
        : 'Choisir le fichier et remplacer')
    : 'Choisir le fichier et mettre à jour';

  const handleConfirm = async () => {
    if (strategy === 'wipe' && wipeScope === 'all') {
      const ok = await ask({
        title: `Remplacer tout le planning ${canalLabel} ?`,
        message: `Supprimer tout le planning ${canalLabel} puis importer uniquement le fichier ?`,
        confirmLabel: 'Tout remplacer',
        confirmVariant: 'danger',
      });
      if (!ok) return;
    }
    onConfirm();
  };

  return (
    <>
    <AppModal
      show={show}
      title={title}
      onClose={onClose}
      footer={(
        <>
          <AppButton variant="outline-secondary" onClick={onClose}>Annuler</AppButton>
          <AppButton variant={strategy === 'wipe' ? 'danger' : 'primary'} onClick={handleConfirm}>
            {confirmLabel}
          </AppButton>
        </>
      )}
    >
            <AppAlert variant="info" className="text-sm py-2 mb-3">
              Choisissez d&apos;abord comment importer, puis sélectionnez le fichier
              {' '}<strong>Excel (.xlsx)</strong>.
              <br />
              {sheetHint}
            </AppAlert>
            <p className="fw-semibold mb-2">Comment importer ?</p>
            <div className="d-flex flex-column gap-2">
              <label className={`form-check kkp-import-option${strategy === 'upsert' ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name="importStrategy"
                  className="form-check-input"
                  checked={strategy === 'upsert'}
                  onChange={() => onStrategyChange('upsert')}
                />
                <span>
                  <strong>Mettre à jour l&apos;existant</strong>
                  <small className="d-block text-secondary-light">
                    Met à jour les ventes déjà présentes (même période, client, format, contenu)
                    et ajoute les nouvelles lignes.
                    <strong> Ne supprime pas</strong> les ventes du site absentes du fichier.
                  </small>
                </span>
              </label>
              <label className={`form-check kkp-import-option${strategy === 'wipe' ? ' is-selected' : ''}`}>
                <input
                  type="radio"
                  name="importStrategy"
                  className="form-check-input"
                  checked={strategy === 'wipe'}
                  onChange={() => onStrategyChange('wipe')}
                />
                <span>
                  <strong>Remplacer par le fichier</strong>
                  <small className="d-block text-secondary-light">
                    Supprime d&apos;abord les ventes {canalLabel} concernées, puis importe le fichier.
                    À utiliser pour que le planning colle à Excel.
                  </small>
                </span>
              </label>
              {strategy === 'wipe' && (
                <div className="ms-1 d-flex flex-column gap-2">
                  {allowYearScope && (
                    <label className={`form-check kkp-import-option kkp-import-option--nested${wipeScope === 'year' ? ' is-selected' : ''}`}>
                      <input
                        type="radio"
                        name="importWipeScope"
                        className="form-check-input"
                        checked={wipeScope === 'year'}
                        onChange={() => onWipeScopeChange('year')}
                      />
                      <span>
                        <strong>Uniquement l&apos;année des filtres</strong>
                        <small className="d-block text-secondary-light">
                          {periodLabel
                            ? `Supprime ${periodLabel}, puis importe les lignes de cette année.`
                            : 'Supprime l’année affichée, puis importe les lignes de cette année.'}
                        </small>
                      </span>
                    </label>
                  )}
                  <label className={`form-check kkp-import-option kkp-import-option--nested${wipeScope === 'month' ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="importWipeScope"
                      className="form-check-input"
                      checked={wipeScope === 'month'}
                      onChange={() => onWipeScopeChange('month')}
                    />
                    <span>
                      <strong>Uniquement le mois des filtres</strong>
                      <small className="d-block text-secondary-light">
                        {periodLabel
                          ? `Supprime ${periodLabel}, puis importe uniquement les lignes de ce mois.`
                          : 'Supprime le mois affiché, puis importe uniquement les lignes de ce mois.'}
                      </small>
                    </span>
                  </label>
                  <label className={`form-check kkp-import-option kkp-import-option--nested${wipeScope === 'all' ? ' is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="importWipeScope"
                      className="form-check-input"
                      checked={wipeScope === 'all'}
                      onChange={() => onWipeScopeChange('all')}
                    />
                    <span>
                      <strong>Tout le planning</strong>
                      <small className="d-block text-secondary-light">
                        Supprime toutes les ventes {canalLabel}, puis importe le fichier entier.
                      </small>
                    </span>
                  </label>
                </div>
              )}
            </div>
    </AppModal>
    {modal}
    </>
  );
};

export default KkpPlanningImportModal;
